import { useState, useEffect, memo } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { tr } from "date-fns/locale/tr";
import { Eye, TrendingUp, Clock } from "lucide-react";
import axios from "axios";
import { useLang } from "../context/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PopularNews = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const { lang, t } = useLang();

  useEffect(() => {
    const fetchPopular = async () => {
      try {
        const response = await axios.get(`${API}/news/popular?limit=5&language=${lang}`);
        setNews(response.data);
      } catch (e) {
        console.error("Error fetching popular news:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchPopular();
  }, [lang]);

  if (loading) {
    return (
      <div className="mb-10">
        <div className="flex items-center gap-2 mb-6">
          <TrendingUp className="w-6 h-6 text-amber-500" />
          <h2 className="text-2xl font-bold text-white">{t('mostRead')}</h2>
        </div>
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex gap-4 p-4 bg-zinc-900/50 rounded-xl animate-pulse">
              <div className="w-12 h-12 bg-zinc-800 rounded-xl" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-zinc-800 rounded w-3/4" />
                <div className="h-3 bg-zinc-800 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (news.length === 0) return null;

  return (
    <div className="mb-10" data-testid="popular-news">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">{t('mostRead')}</h2>
            <p className="text-zinc-500 text-sm">{t('popularThisWeek')}</p>
          </div>
        </div>
      </div>

      {/* Popular News List */}
      <div className="bg-zinc-900/30 backdrop-blur-sm border border-zinc-800/50 rounded-2xl overflow-hidden">
        {news.map((item, index) => (
          <Link
            key={item.id}
            to={`/haber/${item.slug || item.id}`}
            className="flex items-center gap-4 p-4 hover:bg-zinc-800/50 transition-colors border-b border-zinc-800/50 last:border-b-0 group"
            data-testid={`popular-news-${index}`}
          >
            {/* Rank Number */}
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-lg flex-shrink-0 ${
              index === 0 
                ? 'bg-gradient-to-br from-amber-500 to-orange-600 text-white' 
                : index === 1 
                  ? 'bg-gradient-to-br from-zinc-400 to-zinc-500 text-white'
                  : index === 2
                    ? 'bg-gradient-to-br from-amber-700 to-amber-800 text-white'
                    : 'bg-zinc-800 text-zinc-400'
            }`}>
              {index + 1}
            </div>

            {/* Image */}
            <div className="w-20 h-14 rounded-lg overflow-hidden flex-shrink-0">
              <img
                src={item.image_url}
                alt={item.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                onError={(e) => {
                  e.target.src = 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=200&h=140&fit=crop';
                }}
              />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <h3 className="text-white font-medium line-clamp-2 group-hover:text-amber-400 transition-colors">
                {item.title}
              </h3>
              <div className="flex items-center gap-3 mt-1 text-xs text-zinc-500">
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {format(new Date(item.created_at), "d MMM", { locale: tr })}
                </span>
                <span className="flex items-center gap-1 text-amber-500">
                  <Eye className="w-3 h-3" />
                  {item.views.toLocaleString()}
                </span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default memo(PopularNews);
